import { RepositoryFactory } from '@/api/RepositoryFactory';
import { ActionTree, Module } from 'vuex';
import ProjectRepository from '@/api/ProjectRepository';
import Project from '@/models/Project';
import Customer from '@/models/Customer';
import { ProjectState } from '@/store/storeStateInterface';

const projectRepository: ProjectRepository = RepositoryFactory.get('project');

const store: ProjectState = {
    allProjects: [],
    currentProject: undefined
};

const actions: ActionTree<any, any> = {
    getProjectAction: async ({ commit }, payload: { projectId: string }): Promise<Project> => {
        const projectData = await projectRepository.getProject(payload.projectId);
        const project = Project.parseFromObject(projectData);
        commit('currentProject', { project });
        return project;
    },
    /* for init and Reloading ProjectView/CustomerDetailView */
    getAllProjectsAction: async ({ commit }, payload: { customer: Customer }): Promise<Project[]> => {
        // Fetch projects if not already cached
        const projectData = await projectRepository.getAllProjects(payload.customer);
        commit('clearProjects');
        const projects = Project.parseFromArray(projectData) as Project[];
        commit('saveAllProjects', { projects });
        return projects;
    },

    getTotalProjectsAmountAction: async (): Promise<number> => {
        const projectData = await projectRepository.getTotalProjectsAmount() ?? 0;
        return projectData;
    },

    /** Fetches actuator label counts per year in one request. Returns { '2023': [...], '2024': [...], '2025': [...] }. */
    getActuatorLabelCountsAction: async (): Promise<Record<string, Array<{ label: string; count: number }>>> => {
        return projectRepository.getActuatorLabelCounts();
    },

    // eslint-disable-next-line
    createProjectAction: async ({ commit }, payload: { project: Project, customer: Customer }): Promise<Project> => {
        try {
            const projectData = await projectRepository.createProject(payload.project, payload.customer);
            return Project.parseFromObject(projectData);
        } catch (error) {
            console.error('Error in createProjectAction:', error);
            throw error;
        }
    },
    // eslint-disable-next-line
    updateProjectAction: async ({ commit }, payload: { project: Project }): Promise<Project> => {
        const projectData = await projectRepository.updateProject(payload.project);
        const updatedProject = Project.parseFromObject(projectData);
        commit('updateAllProjects', { project: updatedProject });
        return updatedProject;
    },
    deleteProjectAction: async ({ commit }, payload: { projectId: string }): Promise<Project> => {
        const projectData = await projectRepository.deleteProject(payload.projectId);
        const deletedProject = Project.parseFromObject(projectData);
        commit('removeProject', { project: deletedProject });
        return deletedProject;
    },
    deleteAllActuators: async (_, payload: { project: Project }): Promise<Project> => {
        const updatedProject = await projectRepository.deleteAllActuators(payload.project.id);
        const dProject = Project.parseFromObject(updatedProject);
        return dProject;
    },
    resetProjectsAction: async ({ commit }) => {
        commit('clearProjects');
    }
};

const mutations = {
    /* via `getAllProjectsAction` for Reloading ProjectView/CustomerDetailView */
    saveAllProjects: (state: ProjectState, payload: { projects: Project[] }) => {
        state.allProjects = payload.projects;
    },
    removeProject: (state: ProjectState, payload: { project: Project }) => {
        state.allProjects = state.allProjects!.filter(project => project.id !== payload.project.id);
    },
    updateAllProjects: (state: ProjectState, payload: { project: Project }) => {
        const foundIndex = state.allProjects.findIndex(element => element.id === payload.project.id);
        state.allProjects.splice(foundIndex, 1, payload.project);
    },
    currentProject: (state: ProjectState, payload: { project: Project }) => {
        state.currentProject = payload.project;
    },
    clearProjects(state: ProjectState) {
        state.allProjects = [];
    }
};

const getters = {
    allProjects: (state: ProjectState) => {
        return state.allProjects;
    }
};

const projectStore: Module<any, any> = {
    namespaced: true,
    state: store,
    actions,
    mutations,
    getters
};

export default projectStore;
